import React, { useState } from "react";
import { Badge, Button, Card, Dropdown, Form, InputGroup, Spinner } from "react-bootstrap";
import { Diagram3, PlusLg, Search } from "react-bootstrap-icons";
import AccountModeCard from "../AccountModeCard/AccountModeCard";
import { AdminUserModel, ModeGrantModel } from "../../models/AccountPermissionModel";
import { CategoryJsonModel, LicenseJsonModel, ModeJsonModel } from "../../models/BlockTransportSystemJsonModel";
import { colorFromString } from "../../utils/ColorFromString";

interface IProps {
  num: number;
  edited: AdminUserModel;
  setEdited: React.Dispatch<React.SetStateAction<AdminUserModel>>;
  modes: ModeJsonModel[];          // 추가할 수 있는 모드 목록 (드롭다운 후보)
  categories: CategoryJsonModel[]; // 전체 카테고리 목록 — 부여된 칩의 id를 이름으로 보여줄 때 참조
  deletedCategories: CategoryJsonModel[]; // 삭제된 카테고리 목록
  licenses: LicenseJsonModel[];    // 전체 라이선스 목록 — 부여된 칩의 id를 이름으로 보여줄 때 참조
  isAccentCards?: boolean;         // 모드 카드 좌측에 모드 색 띠 표시 여부
  isLoading?: boolean;             // 선택한 계정의 부여 현황을 불러오는 중인지
}

// 모드 추가 드롭다운 내용
const ModeAdder = ({ addable, onAdd }: { addable: ModeJsonModel[]; onAdd: (id: string, isInclude: boolean) => void }) => {
  const [search, setSearch] = useState("");
  const [isInclude, setIsInclude] = useState(true);
  const filtered = addable.filter((mode) => mode.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ width: 320 }}>
      <div className="p-2 border-bottom">
        <Form.Control autoFocus size="sm" placeholder="모드 이름 검색…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="px-3 py-2 border-bottom">
        <Form.Check
          type="checkbox"
          id="mode-add-include"
          checked={isInclude}
          onChange={() => setIsInclude((prev) => !prev)}
          label={
            <span>
              포함된 카테고리도 함께 추가
            </span>
          }
        />
      </div>
      {/* 모드 5개 이상이면 목록을 스크롤로 선택 */}
      <div style={filtered.length >= 5 ? { maxHeight: 200, overflowY: "auto" } : undefined}>
        {filtered.length === 0 && <Dropdown.ItemText className="text-muted small">추가할 모드가 없습니다</Dropdown.ItemText>}
        {filtered.map((mode) => (
          <Dropdown.Item key={mode.id} className="d-flex align-items-center gap-2" onClick={() => onAdd(mode.id, isInclude)}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: colorFromString(mode.id ?? mode.name), flexShrink: 0 }} />
            <span>
              <b>{mode.name}</b>
              <small className="text-muted ms-1">노출 카테고리 {mode.exposureCategoryIds.length}</small>
            </span>
          </Dropdown.Item>
        ))}
      </div>
    </div>
  );
};

const AccountDataAccessCard = ({
  num,
  edited,
  setEdited,
  modes,
  categories,
  deletedCategories,
  licenses,
  isAccentCards,
  isLoading }: IProps) => {
  const [search, setSearch] = useState("");
  const [openSet, setOpenSet] = useState<Set<string>>(() => new Set(edited.modes.slice(0, 1).map((grant) => grant.modeId)));

  const grantedIds = edited.modes.map((grant) => grant.modeId);
  const addable = modes.filter((mode) => !grantedIds.includes(mode.id));
  // 카탈로그에 없는(게시 해제된) 모드는 표시에서 제외
  const visibleGrants = edited.modes.filter((grant) => {
    const mode = modes.find((candidate) => candidate.id === grant.modeId);
    return mode && mode.name.toLowerCase().includes(search.toLowerCase());
  });

  const updateGrant = (modeId: string, fn: (grant: ModeGrantModel) => ModeGrantModel) =>
    setEdited((prev) => ({ ...prev, modes: prev.modes.map((grant) => (grant.modeId === modeId ? fn(grant) : grant)) }));

  const addMode = (modeId: string, isInclude: boolean) => {
    const mode = modes.find((candidate) => candidate.id === modeId);
    if (!mode) return;
    const categoryIds = isInclude ? [...mode.exposureCategoryIds] : [];
    setEdited((prev) => ({
      ...prev,
      modes: [...prev.modes, { modeId, isIncludeCategories: isInclude, categories: categoryIds, licenses: [] }],
    }));
    setOpenSet((prev) => new Set([...prev, modeId]));
  };
  const removeMode = (modeId: string) => setEdited((prev) => ({ ...prev, modes: prev.modes.filter((grant) => grant.modeId !== modeId) }));
  const toggleOpen = (modeId: string) =>
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.has(modeId) ? next.delete(modeId) : next.add(modeId);
      return next;
    });

  return (
    <Card className="shadow-sm mb-3">
      <Card.Header className="bg-white d-flex align-items-center gap-2">
        <Badge bg="primary" pill>{num}</Badge>
        <span className="fw-bold">데이터 접근 권한</span>
        <span className="text-muted small d-none d-md-inline">모드 선택 → 모드별 카테고리 · 라이선스</span>
        <span className="flex-grow-1" />
        <Badge bg="light" text="dark">모드 {edited.modes.length}</Badge>
      </Card.Header>
      <Card.Body>
        <div className="d-flex align-items-center gap-2 mb-3">
          <InputGroup size="sm" style={{ maxWidth: 280 }}>
            <InputGroup.Text className="bg-white"><Search size={14} /></InputGroup.Text>
            <Form.Control placeholder="부여된 모드 검색…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </InputGroup>
          <span className="flex-grow-1" />
          <Dropdown align="end">
            <Dropdown.Toggle variant="primary" size="sm" className="d-flex align-items-center gap-1">
              <PlusLg size={14} />고객사모드(스크린) 추가
            </Dropdown.Toggle>
            {/* renderOnMount: popper 인스턴스를 첫 오픈 전에 생성 → 첫 클릭 시 좌상단 깜빡임 방지 */}
            <Dropdown.Menu renderOnMount style={{ padding: 0 }} popperConfig={{ strategy: "fixed" }}>
              <ModeAdder addable={addable} onAdd={addMode} />
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {isLoading && (
          <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: 200 }}>
            <Spinner animation="border" className="mb-2" />권한 불러오는 중…
          </div>
        )}

        {!isLoading && edited.modes.length === 0 && (
          <div className="text-center text-muted py-4 border rounded bg-light">
            <Diagram3 size={26} className="opacity-50 mb-2" />
            <div className="fw-semibold">부여된 모드가 없습니다</div>
            <div className="small mt-1">“고객사모드(스크린) 추가”로 권한 묶음을 선택하세요. 카테고리·라이선스는 그 안에서 관리됩니다.</div>
          </div>
        )}

        {!isLoading && visibleGrants.map((grant) => (
          <AccountModeCard
            key={grant.modeId}
            mode={modes.find((candidate) => candidate.id === grant.modeId)}
            grant={grant}
            categories={categories}
            deletedCategories={deletedCategories}
            licenses={licenses}
            isOpen={openSet.has(grant.modeId)}
            isAccentCards={isAccentCards}
            onToggleOpen={() => toggleOpen(grant.modeId)}
            update={(fn) => updateGrant(grant.modeId, fn)}
            onRemove={() => removeMode(grant.modeId)}
          />
        ))}
        {edited.modes.length > 0 && visibleGrants.length === 0 && (
          <div className="text-center text-muted py-3">“{search}” 검색 결과가 없습니다.</div>
        )}
      </Card.Body>
    </Card>
  );
};

export default AccountDataAccessCard;
